/**
 * activeWorkflowGuard — Detects whether the user is in an active appraisal/photo workflow.
 *
 * Used by route guards, auth handlers, and subscription checks to prevent
 * destructive redirects (to /auth, /dashboard, /subscribe) while a driver
 * or admin is mid-appraisal.
 *
 * RULE: If isActiveWorkflow() returns true, transient auth/subscription
 * failures MUST NOT navigate away. Only an explicit SIGNED_OUT event or
 * a confirmed (non-transient) access denial may interrupt.
 *
 * LIFECYCLE:
 *   setActiveWorkflow()    — called on StepBasedAppraisal mount
 *   touchActiveWorkflow()  — called on photo capture, form changes, step transitions
 *   completeWorkflow()     — called on successful appraisal submission
 *   cancelWorkflow()       — called on explicit user cancellation / exit
 *   abandonWorkflow()      — called on error boundary when user navigates away
 *   clearActiveWorkflow()  — low-level cleanup (unmount fallback)
 *
 * STALE GUARD:
 *   getActiveWorkflow() auto-expires entries >30 min since lastActivityAt.
 *   This is a FALLBACK — explicit end states should always be used.
 */

import { logger } from "@/lib/logger";

const ACTIVE_WORKFLOW_KEY = "tm_active_workflow";
const STALE_TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes — fallback timeout

export type WorkflowEndReason = "completed" | "cancelled" | "abandoned" | "error_recovery" | "stale_timeout" | "unmount";

export interface ActiveWorkflow {
  /** The appraisal ID currently in progress */
  appraisalId: string;
  /** The job ID the appraisal belongs to */
  jobId: string;
  /** The current step in the appraisal flow */
  currentStep: string;
  /** User role at time of entry */
  role: "owner" | "driver" | "admin";
  /** Route the user is on (e.g. /resume-job/abc123) */
  route: string;
  /** Timestamp when workflow started */
  startedAt: number;
  /** Timestamp of last activity (photo, form input, etc.) */
  lastActivityAt: number;
  /** Owner of the workflow (auth user id) — recovery ignores other users' entries. */
  userId?: string;
}

/**
 * Mark the start of an active appraisal workflow.
 * Called when a user enters StepBasedAppraisal.
 */
export function setActiveWorkflow(workflow: Omit<ActiveWorkflow, "startedAt" | "lastActivityAt" | "userId"> & { userId?: string }): void {
  try {
    const entry: ActiveWorkflow = {
      ...workflow,
      userId: workflow.userId ?? readStoredUserId(),
      startedAt: Date.now(),
      lastActivityAt: Date.now(),
    };
    localStorage.setItem(ACTIVE_WORKFLOW_KEY, JSON.stringify(entry));
    logger.info("[WorkflowGuard] SET:", workflow.appraisalId, "step:", workflow.currentStep, "role:", workflow.role);
  } catch {
    // localStorage unavailable — no-op
  }
}

/**
 * Update the last activity timestamp and optionally the current step.
 * Called on photo capture, form changes, etc.
 */
export function touchActiveWorkflow(updates?: { currentStep?: string }): void {
  try {
    const raw = localStorage.getItem(ACTIVE_WORKFLOW_KEY);
    if (!raw) return;
    const workflow: ActiveWorkflow = JSON.parse(raw);
    workflow.lastActivityAt = Date.now();
    if (updates?.currentStep) workflow.currentStep = updates.currentStep;
    localStorage.setItem(ACTIVE_WORKFLOW_KEY, JSON.stringify(workflow));
  } catch {
    // no-op
  }
}

// ── EXPLICIT END STATES ─────────────────────────────────────────

/**
 * Mark workflow as completed — appraisal was successfully submitted.
 * This is the PRIMARY cleanup path. Do not rely on unmount.
 */
export function completeWorkflow(appraisalId: string): void {
  try {
    const raw = localStorage.getItem(ACTIVE_WORKFLOW_KEY);
    if (raw) {
      const wf: ActiveWorkflow = JSON.parse(raw);
      if (wf.appraisalId === appraisalId) {
        markAppraisalEnded(appraisalId);
        localStorage.removeItem(ACTIVE_WORKFLOW_KEY);
        logger.info("[WorkflowGuard] COMPLETED:", appraisalId);
      }
    }
  } catch {
    // no-op
  }
}

/**
 * Mark workflow as cancelled — user explicitly chose to exit.
 */
export function cancelWorkflow(reason?: string): void {
  try {
    const raw = localStorage.getItem(ACTIVE_WORKFLOW_KEY);
    if (raw) {
      const wf: ActiveWorkflow = JSON.parse(raw);
      logger.info("[WorkflowGuard] CANCELLED:", wf.appraisalId, "reason:", reason ?? "user_exit");
    }
    localStorage.removeItem(ACTIVE_WORKFLOW_KEY);
  } catch {
    // no-op
  }
}

/**
 * Mark workflow as abandoned — error boundary or crash recovery forced exit.
 * Draft data is NOT cleared — just the guard.
 */
export function abandonWorkflow(reason: string): void {
  try {
    const raw = localStorage.getItem(ACTIVE_WORKFLOW_KEY);
    if (raw) {
      const wf: ActiveWorkflow = JSON.parse(raw);
      logger.warn("[WorkflowGuard] ABANDONED:", wf.appraisalId, "reason:", reason);
      markAppraisalEnded(wf.appraisalId);
    }
    localStorage.removeItem(ACTIVE_WORKFLOW_KEY);
  } catch {
    // no-op
  }
}

/**
 * Low-level cleanup — used as unmount fallback only.
 * Prefer completeWorkflow / cancelWorkflow / abandonWorkflow for explicit state.
 */
export function clearActiveWorkflow(): void {
  try {
    localStorage.removeItem(ACTIVE_WORKFLOW_KEY);
    logger.debug("[WorkflowGuard] CLEARED (unmount fallback)");
  } catch {
    // no-op
  }
}

/**
 * Returns the active workflow if one exists and is recent (< 30 minutes).
 * Stale workflows (e.g. from a crash 2 hours ago) are auto-cleaned.
 */
export function getActiveWorkflow(): ActiveWorkflow | null {
  try {
    const raw = localStorage.getItem(ACTIVE_WORKFLOW_KEY);
    if (!raw) return null;
    const workflow: ActiveWorkflow = JSON.parse(raw);
    const age = Date.now() - workflow.lastActivityAt;
    if (age > STALE_TIMEOUT_MS) {
      localStorage.removeItem(ACTIVE_WORKFLOW_KEY);
      logger.info("[WorkflowGuard] STALE_TIMEOUT:", workflow.appraisalId, "age:", Math.round(age / 60000), "min");
      return null;
    }
    return workflow;
  } catch {
    return null;
  }
}

/**
 * Returns true if the user is currently in an active appraisal/photo workflow.
 * This is the primary guard check used by route guards and subscription checks.
 */
export function isActiveWorkflow(): boolean {
  return getActiveWorkflow() !== null;
}

/**
 * Check if a specific route is an appraisal-related route.
 */
export function isAppraisalRoute(pathname: string): boolean {
  return (
    pathname.startsWith("/resume-job/") ||
    pathname.startsWith("/appraisal/") ||
    pathname.startsWith("/job/")
  );
}

const RECOVERABLE_ROUTE = /^\/(resume-job|appraisal|job)\/[A-Za-z0-9-]+$/;

/**
 * 1.72 startup recovery: the active workflow only if it is fresh (not expired;
 * completed/cancelled workflows are removed), has a valid appraisal route and
 * belongs to `userId`. Legacy entries without an owner are ignored.
 */
export function getRecoverableWorkflow(userId: string | null | undefined): ActiveWorkflow | null {
  if (!userId) return null;
  const wf = getActiveWorkflow();
  if (!wf) return null;
  if (!wf.userId || wf.userId !== userId) return null;
  if (typeof wf.route !== "string" || !RECOVERABLE_ROUTE.test(wf.route)) return null;
  return wf;
}

function readStoredUserId(): string | undefined {
  try {
    const raw = localStorage.getItem("sb-wfsbisepapnnnqyyekip-auth-token");
    if (!raw) return undefined;
    const id = JSON.parse(raw)?.user?.id;
    return typeof id === "string" ? id : undefined;
  } catch {
    return undefined;
  }
}

// ── MANDATORY-UPDATE PROTECTION (B2) ────────────────────────────
// Appraisal IDs explicitly finished on this device (completed or abandoned).
// A lingering draft for one of these never counts as protected work.
const ENDED_APPRAISALS_KEY = "tm_ended_appraisals";
const ENDED_APPRAISALS_MAX = 50;

function readEndedAppraisals(): string[] {
  try {
    const v = JSON.parse(localStorage.getItem(ENDED_APPRAISALS_KEY) || "[]");
    return Array.isArray(v) ? v.filter((x) => typeof x === "string") : [];
  } catch {
    return [];
  }
}

export function markAppraisalEnded(appraisalId: string | undefined | null): void {
  if (!appraisalId) return;
  try {
    const list = readEndedAppraisals().filter((x) => x !== appraisalId);
    list.push(appraisalId);
    localStorage.setItem(ENDED_APPRAISALS_KEY, JSON.stringify(list.slice(-ENDED_APPRAISALS_MAX)));
  } catch {
    /* no-op */
  }
}

const DRAFT_KEY = /^appraisalDraft:([A-Za-z0-9-]+):([A-Za-z0-9-]+)$/;

/**
 * True when a saved, resumable appraisal draft exists in localStorage
 * (the existing `useAppraisalDraft` store), regardless of idle time.
 * Drafts are removed by clearDraft() on completion; IDs in the ended list
 * (completed/abandoned) are excluded. Optionally scoped to one job.
 */
export function hasResumableAppraisalDraft(jobId?: string): boolean {
  try {
    const ended = new Set(readEndedAppraisals());
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      const m = k ? DRAFT_KEY.exec(k) : null;
      if (!m) continue;
      if (jobId && m[1] !== jobId) continue;
      if (ended.has(m[2])) continue;
      const raw = localStorage.getItem(k!);
      if (!raw) continue;
      try {
        const d = JSON.parse(raw);
        if (d && typeof d === "object") return true;
      } catch {
        /* unreadable draft — ignore */
      }
    }
  } catch {
    /* storage unavailable */
  }
  return false;
}

/**
 * Final, synchronous decision used immediately before rendering the
 * mandatory-update blocker. Protected = active workflow (<30 min) OR a
 * resumable saved draft. On an appraisal route only that job's draft counts,
 * so starting a NEW appraisal stays blocked. Read errors → protected (fail open).
 */
export function isProtectedAppraisalWork(pathname: string): boolean {
  try {
    if (isActiveWorkflow()) return true;
    const m = /^\/(?:resume-job|appraisal|job)\/([A-Za-z0-9-]+)/.exec(pathname || "");
    return hasResumableAppraisalDraft(m ? m[1] : undefined);
  } catch {
    return true;
  }
}
